import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FunnelStatus,
  CrmFunnelsStatusesService,
  FunnelStatusCreateBodyParams,
  FunnelStatusUpdateBodyParams,
  FunnelStatusBulkUpdateBodyParams,
} from 'src/api/generated';
import {
  getModelFromCache,
  updateCache,
} from 'src/modules/shared/utils/cache/update.util';
import {
  updateFunnelStatusCache,
  getFunnelStatusCacheSnapshot,
} from 'src/modules/shared/utils/cache/funnel-statuses/update.util';
import {
  createOptimisticFunnelStatus,
  UpdateFunnelStatusContext,
} from 'src/modules/shared/utils/cache/funnel-statuses/optimistic.util';

interface UseCreateFunnelStatusMutationResponse {
  createStatus: (data: FunnelStatusCreateBodyParams) => Promise<FunnelStatus>;
  loading: boolean;
  error: Error | null;
}

export const useCreateFunnelStatus = (
  onSuccess: (status: FunnelStatus) => void = () => {}
): UseCreateFunnelStatusMutationResponse => {
  const queryClient = useQueryClient();

  const createStatus = async (
    data: FunnelStatusCreateBodyParams
  ): Promise<FunnelStatus> => {
    return await CrmFunnelsStatusesService.createFunnelStatus(data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: createStatus,
    onSuccess: (status) => {
      queryClient.invalidateQueries({ queryKey: ['crm/funnels/statuses'] });
      onSuccess(status);
    },
  });

  return {
    createStatus: mutateAsync,
    loading,
    error,
  };
};

interface UseUpdateFunnelStatusMutationResponse {
  updateStatus: (
    params: { id: number } & FunnelStatusUpdateBodyParams
  ) => Promise<FunnelStatus>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateFunnelStatus = (
  onSuccess: (status: FunnelStatus) => void = () => {},
  onError: (error: Error, context: UpdateFunnelStatusContext) => void = () => {}
): UseUpdateFunnelStatusMutationResponse => {
  const queryClient = useQueryClient();

  const updateStatus = async ({
    id,
    ...data
  }: { id: number } & FunnelStatusUpdateBodyParams): Promise<FunnelStatus> => {
    return await CrmFunnelsStatusesService.updateFunnelStatus(id, data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: updateStatus,
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['crm/funnels/statuses'] });

      const snapshotQueries = await getFunnelStatusCacheSnapshot(queryClient);

      const previousStatus = getModelFromCache<FunnelStatus>(
        queryClient,
        ['crm/funnels/statuses'],
        params.id
      );

      const optimisticStatus = createOptimisticFunnelStatus(
        { id: params.id, data: params },
        previousStatus
      );

      const context: UpdateFunnelStatusContext = {
        params: { id: params.id, data: params },
        previousStatus,
      };
      updateFunnelStatusCache(queryClient, optimisticStatus);

      return { snapshotQueries, context };
    },
    onError: (error, _params, mutationContext) => {
      if (mutationContext?.snapshotQueries) {
        mutationContext.snapshotQueries.forEach(({ queries }) => {
          queries.forEach(([queryKey, queryData]) => {
            queryClient.setQueryData(queryKey, queryData);
          });
        });
      }

      if (mutationContext?.context) {
        onError(error, mutationContext.context);
      }
    },
    onSuccess: (updatedStatus) => {
      updateFunnelStatusCache(queryClient, updatedStatus);

      onSuccess(updatedStatus);
    },
  });

  return {
    updateStatus: mutateAsync,
    loading,
    error,
  };
};

interface UseDeleteFunnelStatusMutationResponse {
  deleteStatus: (id: number) => Promise<FunnelStatus>;
  loading: boolean;
  error: Error | null;
}

export const useDeleteFunnelStatus = (
  onSuccess: (status: FunnelStatus) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseDeleteFunnelStatusMutationResponse => {
  const queryClient = useQueryClient();

  const deleteStatus = async (id: number): Promise<FunnelStatus> => {
    return await CrmFunnelsStatusesService.deleteFunnelStatus(id);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: deleteStatus,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['crm/funnels/statuses'] });

      const snapshotQueries = await getFunnelStatusCacheSnapshot(queryClient);

      const previousStatus = getModelFromCache<FunnelStatus>(
        queryClient,
        ['crm/funnels/statuses'],
        id
      );

      if (previousStatus) {
        updateCache({
          queryClient,
          queryKey: ['crm/funnels/statuses'],
          updatedData: previousStatus,
          shouldRemoveItem: (_matchedKey, item) => item.id === id,
        });
      }

      queryClient.removeQueries({
        queryKey: ['crm/funnels/statuses', id],
        exact: true,
      });

      return { snapshotQueries, previousStatus };
    },
    onError: (error, _id, result) => {
      if (result?.snapshotQueries) {
        result.snapshotQueries.forEach(({ queries }) => {
          queries.forEach(([queryKey, queryData]) => {
            queryClient.setQueryData(queryKey, queryData);
          });
        });
      }

      if (error) {
        onError(error);
      }
    },
    onSuccess: (status, _id, _result) => {
      queryClient.invalidateQueries({ queryKey: ['crm/funnels/statuses'] });
      onSuccess(status);
    },
  });

  return {
    deleteStatus: mutateAsync,
    loading,
    error,
  };
};

interface UseBulkUpdateFunnelStatusesMutationResponse {
  bulkUpdateStatuses: (
    data: FunnelStatusBulkUpdateBodyParams
  ) => Promise<FunnelStatus[]>;
  loading: boolean;
  error: Error | null;
}

export const useBulkUpdateFunnelStatuses = (
  onSuccess: (statuses: FunnelStatus[]) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseBulkUpdateFunnelStatusesMutationResponse => {
  const queryClient = useQueryClient();

  const bulkUpdateStatuses = async (
    data: FunnelStatusBulkUpdateBodyParams
  ): Promise<FunnelStatus[]> => {
    const response =
      await CrmFunnelsStatusesService.bulkUpdateFunnelStatuses(data);
    return response;
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: bulkUpdateStatuses,
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['crm/funnels/statuses'] });

      const snapshotQueries = await getFunnelStatusCacheSnapshot(queryClient);

      if (!data.updates || !Array.isArray(data.updates)) {
        return { snapshotQueries, optimisticStatuses: [] };
      }

      const optimisticStatuses = data.updates.map((update) => {
        const previousStatus = getModelFromCache<FunnelStatus>(
          queryClient,
          ['crm/funnels/statuses'],
          update.id
        );

        const optimisticStatus = createOptimisticFunnelStatus(
          { id: update.id, data: update.data },
          previousStatus
        );

        updateFunnelStatusCache(queryClient, optimisticStatus);

        return optimisticStatus;
      });

      return { snapshotQueries, optimisticStatuses };
    },
    onError: (error, _data, result) => {
      if (result?.snapshotQueries) {
        result.snapshotQueries.forEach(({ queries }) => {
          queries.forEach(([queryKey, queryData]) => {
            queryClient.setQueryData(queryKey, queryData);
          });
        });
      }

      if (error) {
        onError(error);
      }
    },
    onSuccess: (statuses, _data, _result) => {
      statuses.forEach((status) => {
        updateFunnelStatusCache(queryClient, status);
      });

      onSuccess(statuses);
    },
  });

  return {
    bulkUpdateStatuses: mutateAsync,
    loading,
    error,
  };
};
